import { useState } from "react";
import { Check, Eye, EyeOff, Pencil, Trash2, X } from "lucide-react";
import { dayjs } from "@boo/shared";
import type { BankConfig } from "@/services/gmailIntegrationApi";
import { INPUT_CLASS } from "../types/settings.types";

interface Props {
  config: BankConfig;
  onDelete: (id: string) => void;
  onSave: (
    id: string,
    label: string,
    senderPattern: string,
    password: string,
    accountNumber: string,
  ) => Promise<void>;
}

export default function BankConfigRow({ config, onDelete, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(config.label);
  const [senderPattern, setSenderPattern] = useState(config.senderPattern);
  const [password, setPassword] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(config.id, label, senderPattern, password, accountNumber);
      setEditing(false);
      setPassword("");
      setAccountNumber("");
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setEditing(false);
    setLabel(config.label);
    setSenderPattern(config.senderPattern);
    setPassword("");
    setAccountNumber("");
  }

  if (editing) {
    return (
      <li className="py-3 space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Bank name"
            className={`w-full sm:flex-1 ${INPUT_CLASS}`}
          />
          <input
            type="text"
            value={senderPattern}
            onChange={(e) => setSenderPattern(e.target.value)}
            placeholder="Sender pattern (e.g. @ktc.co.th)"
            className={`w-full sm:flex-1 ${INPUT_CLASS}`}
          />
          <div className="relative sm:flex-1">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={
                config.hasPassword ? "New password" : "PDF password (optional)"
              }
              className={`w-full pr-8 sm:flex-1 ${INPUT_CLASS}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          <input
            type="text"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            placeholder={
              config.accountLast4
                ? `Account ····${config.accountLast4} (type to replace)`
                : "Bank account number (optional)"
            }
            className={`w-full sm:flex-1 ${INPUT_CLASS}`}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={handleCancel}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !label.trim() || !senderPattern.trim()}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30 transition-colors disabled:opacity-40"
          >
            <Check className="w-4 h-4" />
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-center justify-between py-3 gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{config.label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {config.senderPattern} ·{" "}
          {config.hasPassword ? "Password set" : "No password"} ·{" "}
          {config.accountLast4 ? `Acct ····${config.accountLast4}` : "No account"}{" "}
          · Added {dayjs(config.createdAt).format("D MMM YYYY")}
        </p>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button
          onClick={() => setEditing(true)}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label={`Edit ${config.label}`}
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(config.id)}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
          aria-label={`Delete ${config.label}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}
